/**
 * <StudentLiveEndPage> — le tuteur a clôturé le live (page).
 *
 * L'écran d'aujourd'hui (`EndSessionScreenStudent`) : « Ton masteur a mis fin à la session LIVE »,
 * l'activité mise en pause s'il y en avait une, le rapport du jour, « Quitter le live ». Puis,
 * pour un live de plus de quinze minutes, la note (`rating`, `<LiveRatingModal>`).
 *
 * Le rapport n'a pas de forme élève : l'ancien code montre à l'élève le même rapport qu'au
 * parent. La page compose donc les briques du rapport du parent qui se lisent à la deuxième
 * personne comme à la troisième : son en-tête (`<LiveReportHeader>` : les chapitres, le tuteur,
 * l'heure, l'anneau), le mot du tuteur, le score d'engagement (`<LiveEngagementScoreCard>`) et le
 * travail validé ou à faire (`<SessionActivitiesCard>`). Le bilan commenté et le rendez-vous avec
 * le conseiller, écrits pour le parent, n'y sont pas.
 *
 * « Quitter le live » n'est plus rouge : le live est fini, sortir est le geste attendu, pas une
 * destruction. Contrôlée : le rapport, la pause, la sortie et la note arrivent en props.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import {
    type UILiveSessionReport,
    type UILiveSessionReportActivity,
    type UILiveSessionReportDetails,
} from "../../../ports/primary/ui/types"
import { type UILiveEndScores } from "../../../ports/primary/ui/types/studentLive"
import { Button } from "../../atoms/Buttons/Button"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { LiveReportHeader } from "../../molecules/LiveReportHeader"
import { LiveReportHeaderSkeleton } from "../../molecules/LiveReportHeader/LiveReportHeaderSkeleton"
import { LiveEngagementScoreCard } from "../../organisms/LiveEngagementScoreCard"
import { ReportChapterSectionCard } from "../../organisms/ReportChapterSectionCard"
import { ReportConceptsCard } from "../../organisms/ReportConceptsCard"
import { SessionActivitiesCard } from "../../organisms/SessionActivitiesCard"
import { LiveRatingModal, type LiveRatingModalProps } from "../../templates/LiveRatingModal"
import { PageTemplate } from "../../templates/PageTemplate"

// Le score d'engagement du rapport : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveEndScores }

export type StudentLiveEndPageProps = {
    /** L'élève avait une activité en cours : elle est mise en pause. Défaut `false`. */
    activityPaused?: boolean
    /** L'en-tête du rapport du jour. `null` : pas de rapport, la page dit la fin seule. */
    report: UILiveSessionReport | null
    /**
     * Le rapport du jour se charge (l'écran le demande en arrivant, comme aujourd'hui) : sa carte
     * montre sa forme (`<LiveReportHeaderSkeleton>`), le reste attend. Défaut `false`.
     */
    reportLoading?: boolean
    scores?: UILiveEndScores
    validatedActivities?: UILiveSessionReportActivity[]
    todoActivities?: UILiveSessionReportActivity[]
    /**
     * Le mot de Zoé du rapport, ou « Malheureusement, nous n'avons pas eu la chance de te voir… »,
     * comme le rapport de l'ancien écran de fin. Absent : rien.
     */
    heroMessage?: string
    /** Les chapitres travaillés et le contrôle qui s'y rattache. Défaut : aucun. */
    chapterSections?: UILiveSessionReportDetails["chapterSections"]
    /** Les notions abordées (un rapport V1). Absent : pas de carte. */
    conceptGroups?: UILiveSessionReportDetails["conceptGroups"]
    onLeave: () => void
    /** La sortie part. Défaut `false`. */
    leaving?: boolean
    /** La note, posée par-dessus. Absente : pas de note (un live de moins de quinze minutes). */
    rating?: LiveRatingModalProps
    testID?: string
}

/** Une colonne de lecture : le rapport ne s'étale pas sur un grand écran. */
const READING_WIDTH = 640

const TEXTS = {
    ended: "Ton masteur a mis fin à la session LIVE",
    paused: "Ne t'inquiète pas ! Ton activité a été mise en pause.",
    leave: "Quitter le live",
}

const NONE: UILiveSessionReportActivity[] = []
const NO_SECTIONS: UILiveSessionReportDetails["chapterSections"] = []

export function StudentLiveEndPage(props: StudentLiveEndPageProps) {
    const {
        activityPaused = false,
        report,
        reportLoading = false,
        scores,
        validatedActivities = NONE,
        todoActivities = NONE,
        heroMessage,
        chapterSections = NO_SECTIONS,
        conceptGroups,
        onLeave,
        leaving = false,
        rating,
        testID,
    } = props

    const leave = <Button label={TEXTS.leave} onPress={onLeave} loading={leaving} fullWidth />

    return (
        <>
            <PageTemplate maxContentWidth={READING_WIDTH} padding="4" footer={leave} testID={testID}>
                <Stack gap="4">
                    <Stack gap="2" style={styles.intro}>
                        <Title size="text-xl" center>
                            {TEXTS.ended}
                        </Title>
                        {activityPaused && (
                            <Text size="text-md" color="text-muted" center>
                                {TEXTS.paused}
                            </Text>
                        )}
                    </Stack>

                    {reportLoading ? (
                        <DelayedSkeleton>
                            <Card p="4">
                                <LiveReportHeaderSkeleton />
                            </Card>
                        </DelayedSkeleton>
                    ) : (
                        <>
                            {report != null && (
                                <Card p="4">
                                    <Stack gap="3">
                                        <LiveReportHeader report={report} />
                                        {report.comment != null && report.comment !== "" && (
                                            <Text size="text-md">{report.comment}</Text>
                                        )}
                                        {heroMessage != null && heroMessage !== "" && (
                                            <Text size="text-md" color="text-muted">
                                                {heroMessage}
                                            </Text>
                                        )}
                                    </Stack>
                                </Card>
                            )}
                            {conceptGroups != null && <ReportConceptsCard groups={conceptGroups} />}
                            {scores != null && <LiveEngagementScoreCard scores={scores} />}
                            {chapterSections.map((section, index) => (
                                <ReportChapterSectionCard
                                    key={index}
                                    title={section.title}
                                    assessment={section.assessment}
                                    chapters={section.chapters}
                                />
                            ))}
                            <SessionActivitiesCard validated={validatedActivities} todo={todoActivities} />
                        </>
                    )}
                </Stack>
            </PageTemplate>
            {rating != null && <LiveRatingModal {...rating} />}
        </>
    )
}

const styles = StyleSheet.create((theme) => ({
    intro: { paddingVertical: theme.spacing["4"] },
}))
